import React from 'react';
import { Box, Typography } from '@mui/material';
import { CloudQueue, Star, AutoAwesome } from '@mui/icons-material';
import { portal } from '../styles/dashboardBrand';

const DreamyAppsLogo = ({ size = 'medium', showText = true, variant = 'default' }) => {
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return { iconSize: 24, fontSize: '1.2rem', gap: 1 };
      case 'large':
        return { iconSize: 48, fontSize: '2.5rem', gap: 2 };
      case 'xlarge':
        return { iconSize: 64, fontSize: '3rem', gap: 2.5 };
      default:
        return { iconSize: 32, fontSize: '1.8rem', gap: 1.5 };
    }
  };

  const { iconSize, fontSize, gap } = getSizeStyles();

  const getVariantStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          iconColor: 'white',
          textGradient: portal.gradientTextWarm,
        };
      case 'colorful':
        return {
          iconColor: 'linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 50%, #45b7d1 100%)',
          textGradient: portal.gradientTextWarm,
        };
      default:
        return {
          iconColor: 'white',
          textGradient: portal.gradientTextWarm,
        };
    }
  };

  const { iconColor, textGradient } = getVariantStyles();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: gap,
        cursor: 'pointer',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.05)',
        },
      }}
    >
      {/* Logo Icon */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Main cloud icon */}
        <CloudQueue
          sx={{
            fontSize: iconSize,
            color: iconColor,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
            zIndex: 2,
            animation: 'cloudDrift 4s ease-in-out infinite',
            '@keyframes cloudDrift': {
              '0%, 100%': { transform: 'translateX(0px) scale(1)' },
              '25%': { transform: 'translateX(2px) scale(1.02)' },
              '50%': { transform: 'translateX(0px) scale(1.05)' },
              '75%': { transform: 'translateX(-2px) scale(1.02)' },
            },
          }}
        />
        
        {/* Floating stars */}
        <Star
          sx={{
            position: 'absolute',
            fontSize: iconSize * 0.4,
            color: '#ffd700',
            top: -iconSize * 0.1,
            right: -iconSize * 0.1,
            animation: 'starGlow 3s ease-in-out infinite',
            '@keyframes starGlow': {
              '0%, 100%': { opacity: 0.8, filter: 'drop-shadow(0 0 5px #ffd700) brightness(1)' },
              '50%': { opacity: 1, filter: 'drop-shadow(0 0 15px #ffd700) brightness(1.3)' },
            },
            zIndex: 3,
          }}
        />
        <AutoAwesome
          sx={{
            position: 'absolute',
            fontSize: iconSize * 0.3,
            color: '#ff6b6b',
            bottom: -iconSize * 0.1,
            left: -iconSize * 0.1,
            animation: 'sparkleShimmer 2.5s ease-in-out infinite',
            '@keyframes sparkleShimmer': {
              '0%, 100%': { opacity: 0.6, transform: 'scale(1) rotate(0deg)', filter: 'hue-rotate(0deg)' },
              '25%': { opacity: 0.9, transform: 'scale(1.1) rotate(90deg)', filter: 'hue-rotate(90deg)' },
              '50%': { opacity: 1, transform: 'scale(1.2) rotate(180deg)', filter: 'hue-rotate(180deg)' },
              '75%': { opacity: 0.9, transform: 'scale(1.1) rotate(270deg)', filter: 'hue-rotate(270deg)' },
            },
            zIndex: 3,
          }}
        />
      </Box>

      {/* Logo Text */}
      {showText && (
        <Typography
          variant="h4"
          component="div"
          sx={{
            fontSize: fontSize,
            fontWeight: 900,
            background: textGradient,
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '0.05em',
            lineHeight: 1,
            fontStyle: 'italic',
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            textAlign: 'center',
            animation: 'gradientShift 3s ease-in-out infinite, dreamyFloat 6s ease-in-out infinite',
            '@keyframes gradientShift': {
              '0%': { backgroundPosition: '0% 50%' },
              '50%': { backgroundPosition: '100% 50%' },
              '100%': { backgroundPosition: '0% 50%' },
            },
            '@keyframes dreamyFloat': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '25%': { transform: 'translateY(-3px) rotate(1deg)' },
              '50%': { transform: 'translateY(-6px) rotate(0deg)' },
              '75%': { transform: 'translateY(-3px) rotate(-1deg)' },
            },
          }}
        >
          Dreamey Vids
        </Typography>
      )}

    </Box>
  );
};

export default DreamyAppsLogo;
